import { useState } from 'react';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import Button from '@/components/Button';
import { Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { TableCell, TableRow } from '@/elements/Table';
import { PastJob } from '@/services/workersV2API';

type Props = {
  pastJobs: PastJob[];
};

const PER_PAGE = 10;

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

const getStatusBadge = (job: PastJob) => {
  if (job.dismissed_at) {
    return <Badge label="Dismissed" status="danger" />;
  }
  if (job.dropped_at) {
    return <Badge label="Dropped" status="warning" />;
  }
  if (!job.showed) {
    return <Badge label="No Show" status="danger" />;
  }
  if (job.has_approved_time_sheet) {
    return <Badge label="Completed" status="theme" />;
  }
  return <Badge label={job.status} status="neutral" />;
};

const PastJobsCard = ({ pastJobs }: Props) => {
  const [page, setPage] = useState(1);

  if (!pastJobs || pastJobs.length === 0) {
    return (
      <Card title="Past Jobs (0)">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">No past jobs found</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  const totalPages = Math.ceil(pastJobs.length / PER_PAGE);
  const paginatedJobs = pastJobs.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <Card title={`Past Jobs (${pastJobs.length})`}>
      <Table>
        <colgroup>
          <col style={{ width: '190px' }} />
          <col style={{ width: '140px' }} />
          <col style={{ width: '260px' }} />
          <col style={{ width: '170px' }} />
          <col style={{ width: '90px' }} />
          <col style={{ width: '110px' }} />
          <col style={{ width: '90px' }} />
          <col style={{ width: '140px' }} />
        </colgroup>
        <TableHeader
          fields={[
            'Date',
            'Skill',
            'Customer',
            'Location',
            'Hours',
            'Pay Rate',
            'Rating',
            'Status',
          ]}
        />
        <tbody>
          {paginatedJobs.map((job) => (
            <TableRow key={job.id}>
              <TableCell css={{ verticalAlign: 'top' }}>
                <Stack vertical gap={2}>
                  <Small>{formatDate(job.shift_start_at)}</Small>
                  {job.shift_start_at && job.shift_end_at && (
                    <Small color="lighter" css={{ whiteSpace: 'nowrap' }}>
                      {new Date(job.shift_start_at).toLocaleTimeString(
                        'en-US',
                        { hour: 'numeric', minute: '2-digit' },
                      )}{' '}
                      -{' '}
                      {new Date(job.shift_end_at).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </Small>
                  )}
                </Stack>
              </TableCell>
              <TableCell css={{ verticalAlign: 'top' }}>
                <Small>{job.skill_name}</Small>
              </TableCell>
              <TableCell css={{ verticalAlign: 'top' }}>
                <Small>{job.customer_name}</Small>
              </TableCell>
              <TableCell css={{ verticalAlign: 'top' }}>
                {job.address.city && job.address.state ? (
                  <Small>
                    {job.address.city}, {job.address.state}
                  </Small>
                ) : (
                  <Small color="lighter">-</Small>
                )}
              </TableCell>
              <TableCell css={{ verticalAlign: 'top' }}>
                {job.timesheet?.approved_minutes ? (
                  <Small css={{ whiteSpace: 'nowrap' }}>
                    {formatMinutes(job.timesheet.approved_minutes)}
                  </Small>
                ) : (
                  <Small color="lighter">-</Small>
                )}
              </TableCell>
              <TableCell css={{ verticalAlign: 'top' }}>
                <Small css={{ whiteSpace: 'nowrap' }}>
                  ${(job.pay_rate / 100).toFixed(2)}/hr
                </Small>
              </TableCell>
              <TableCell css={{ verticalAlign: 'top' }}>
                {job.timesheet?.rating ? (
                  <Badge label={`⭐ ${job.timesheet.rating}`} status="theme" />
                ) : (
                  <Small color="lighter">-</Small>
                )}
              </TableCell>
              <TableCell css={{ verticalAlign: 'top' }}>
                {getStatusBadge(job)}
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <Card.Section>
          <Stack
            gap={8}
            style={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              a11yLabel="Previous page"
              appearance="outline"
              label="Previous"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            />
            <Small>
              Page {page} of {totalPages}
            </Small>
            <Button
              a11yLabel="Next page"
              appearance="outline"
              label="Next"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            />
          </Stack>
        </Card.Section>
      )}
    </Card>
  );
};

export default PastJobsCard;
